import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { 
  TicketIcon as Ticket,
  BookOpen,
  Users,
  BarChart3,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Plus,
  Settings
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSupportStats } from '../../hooks/support/useSupportData';

// Tab components (will be created next)
import TicketsTab from '../support/TicketsTab';
import KnowledgeBaseTab from '../support/KnowledgeBaseTab';
import TeamTab from '../support/TeamTab';
import AnalyticsTab from '../support/AnalyticsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const SupportHub = () => {
  // URL state management
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab') || 'tickets';
  
  // Component state
  const [activeTab, setActiveTab] = useState(urlTab);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  // Fetch support stats using React Query
  const { 
    data: statsData, 
    isLoading: loading, 
    error, 
    refetch: refetchStats 
  } = useSupportStats();

  const stats = statsData?.stats || null;
  const lastUpdated = statsData ? new Date().toISOString() : null;

  // Sync activeTab with URL
  useEffect(() => {
    setActiveTab(urlTab);
  }, [urlTab]);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const handleRefresh = () => {
    refetchStats();
    toast.success('Support data refreshed', {
      position: 'top-right',
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  // Loading state
  if (loading && !stats) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading Support Hub...</p>
        </div>
      </div>
    );
  }

  // Tab configuration
  const tabs = [
    { 
      id: 'tickets', 
      label: 'Tickets', 
      icon: Ticket,
      description: 'Manage customer support tickets'
    },
    { 
      id: 'kb', 
      label: 'Knowledge Base', 
      icon: BookOpen,
      description: 'Manage help articles and documentation'
    },
    { 
      id: 'team', 
      label: 'Team', 
      icon: Users,
      description: 'Manage support team members'
    },
    { 
      id: 'analytics', 
      label: 'Analytics', 
      icon: BarChart3,
      description: 'View support metrics and performance'
    }
  ];

  return (
    <div className="space-y-6">
      <ToastContainer />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Support Hub</h2>
              <p className="text-gray-600 dark:text-gray-300">
                Manage customer support tickets, knowledge base, and team
              </p>
            </div>
            
            {/* System Status Indicator */}
            <div className="hidden lg:flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/20">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                System Active
              </span>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            size="sm"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Tickets */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tickets</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {stats.total_tickets || 0}
                </p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-lg dark:bg-indigo-900/20">
                <Ticket className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </div>

          {/* Open Tickets */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open Tickets</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {stats.open_tickets || 0}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg dark:bg-yellow-900/20">
                <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          {/* Resolved Today */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Resolved Today</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {stats.resolved_today || 0}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg dark:bg-green-900/20">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          {/* Avg Response Time */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Response Time</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {stats.avg_response_time ? `${stats.avg_response_time}m` : '-'}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg dark:bg-blue-900/20">
                <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error.message || 'Failed to load support data'}</p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'tickets' && <TicketsTab />}
          {activeTab === 'kb' && <KnowledgeBaseTab />}
          {activeTab === 'team' && <TeamTab />}
          {activeTab === 'analytics' && <AnalyticsTab />}
        </div>
      </div>

      {/* Last Updated Timestamp */}
      {lastUpdated && (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          Last updated: {new Date(lastUpdated).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default SupportHub;
